import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { useDevBypassRedirect } from "@/hooks/use-dev-bypass-redirect";
import { NanaeLogoSymbol } from "@/components/qv3/NanaeLogo";
import { socialMeta } from "@/lib/seo-meta";

const schema = z.object({ email: z.string().email("Adresse email invalide") });
type Values = z.infer<typeof schema>;

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Mot de passe oublié — Hōjin" },
      { name: "description", content: "Réinitialisez le mot de passe de votre compte Hōjin." },
      { name: "robots", content: "noindex, follow" },
      ...socialMeta({
        title: "Mot de passe oublié — Hōjin",
        description: "Réinitialisez le mot de passe de votre compte Hōjin.",
        url: "https://hojin.ai/forgot-password",
        image: "conseil",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/forgot-password" }],
  }),
  component: ForgotPasswordPage,
});

export function ForgotPasswordPage() {
  useDevBypassRedirect();
  const { resetPassword } = useAuth();
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await resetPassword(values.email);
      setSent(true);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Envoi impossible");
    }
  });

  return (
    <div className="nanae-auth-shell qv3 qv3-dark">
      <main className="nanae-auth-main">
        <section className="nanae-auth-card" aria-labelledby="forgot-title">
          <span className="nanae-auth-card-corner left">N°RECOUVR · MMXXVI</span>
          {formError && (
            <span className="nanae-auth-card-corner right">
              <span className="qv3-led" aria-hidden="true" />
              ERR
            </span>
          )}

          <div className="nanae-auth-logo">
            <NanaeLogoSymbol size="xl" />
          </div>

          <p className="nanae-auth-eyebrow">§ RECOUVREMENT · DISCRÉTION ABSOLUE</p>
          <h1 id="forgot-title" className="nanae-auth-title">
            Égaré dans les couloirs ?
          </h1>
          <p className="nanae-auth-subtitle">
            Indiquez votre adresse, nous renvoyons les clefs.
          </p>

          {sent ? (
            <p
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontStyle: "italic",
                fontSize: 17,
                color: "var(--qv3-paper)",
                margin: "12px 0 0",
                lineHeight: 1.5,
              }}
            >
              Un message vient de partir. Consultez votre boîte de réception — le lien y figure.
            </p>
          ) : (
            <form onSubmit={onSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div className="qv3-field">
                <label className="qv3-field-label" htmlFor="forgot-email">E-MAIL</label>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  className="qv3-input-lcd"
                  aria-invalid={errors.email ? "true" : "false"}
                  placeholder="vous@domaine.com"
                  {...register("email")}
                />
                {errors.email && <p className="qv3-field-error">— {errors.email.message}</p>}
              </div>

              {formError && (
                <p role="alert" className="nanae-auth-error">
                  <span className="qv3-led" aria-hidden="true" />
                  {formError}
                </p>
              )}

              <button type="submit" className="qv3-btn-hifi" disabled={isSubmitting}>
                {isSubmitting ? "Envoi…" : "Recevoir le lien"}
                {!isSubmitting && <span className="qv3-btn-hifi-hint">ENTER →</span>}
              </button>
            </form>
          )}

          <div className="nanae-auth-altlinks">
            <Link to="/login" className="nanae-auth-altlink">
              <strong>Retour à l'accès</strong>
            </Link>
          </div>
        </section>
      </main>
      <footer className="nanae-auth-foot" data-rp="auth-footer">SEPT VOIX, UNE DIRECTION · MMXXVI</footer>
    </div>
  );
}
